import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CANVAS_MIN, CANVAS_MAX } from '@blackboard/shared';
import { useAuth } from '../state/auth';
import { useBoard } from '../state/board';
import { useTool } from '../state/tool';
import { usePresence } from '../state/presence';
import { getTheme, type ThemeSpec } from '../themes';
import type { Board as BoardType, Tool, Point, Stroke } from '@blackboard/shared';
import { CanvasStage, type BrushGhost } from '../canvas/CanvasStage';
import {
  initViewport, pan, zoomAt, viewportBBox, screenToWorld, clampViewport, ZOOM_MIN, type Viewport,
} from '../canvas/viewport';

type BBoxT = { x1: number; y1: number; x2: number; y2: number };
let boundsCache: { slug: string; bbox: BBoxT; at: number } | null = null;
type StrokeLike = Pick<Stroke, 'size' | 'tool'>;
import { MultiTouchTracker } from '../canvas/gestures';
import { WsClient, type WsStatus } from '../sync/WsClient';
import { useDrawing } from '../sync/useDrawing';
import ConnectionBanner from '../ui/ConnectionBanner';
import OnboardingHint, { shouldShowOnboarding } from '../ui/OnboardingHint';
import { GuidelinesDialog, shouldShowGuidelines } from '../ui/GuidelinesDialog';
import ConfirmDialog from '../ui/ConfirmDialog';
import ToastViewport from '../ui/Toast';
import { useTool as useToolStore } from '../state/tool';
import { useToast } from '../state/toast';
import { useAdminAreaErase } from '../sync/useAdminAreaErase';
import { useUndo } from '../state/undo';
import { loadTile, tilesInBBox, loadDelta, resetTileCache, setCurrentTileScope } from '../sync/loadTiles';
import { Toolbar } from '../ui/Toolbar';
import { UserList } from '../ui/UserList';
import { BoardSwitcher } from '../ui/BoardSwitcher';
import { useStrokeAuthorHover } from '../hooks/useStrokeAuthorHover';
import { EmptyBoardWatermark } from '../ui/EmptyBoardWatermark';
import { AdminEraseOverlay } from '../ui/AdminEraseOverlay';
import { BoardLoadingStates } from '../ui/BoardLoadingStates';

export default function BoardPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const token = useAuth((s) => s.token)!;
  const user = useAuth((s) => s.user)!;
  const logout = useAuth((s) => s.logout);
  const [wsStatus, setWsStatus] = useState<WsStatus>({ t: 'connecting' });
  const [board, setBoard] = useState<BoardType | null>(null);
  const [boardLoadState, setBoardLoadState] = useState<'loading' | 'ok' | 'not_found' | 'error'>('loading');
  const onboardingInitiallyOpen = shouldShowOnboarding();
  const [guidelinesOpen, setGuidelinesOpen] = useState(shouldShowGuidelines);
  const { hoveredAuthor, updateHover, clearHover } = useStrokeAuthorHover();
  const pushToast = useToast((s) => s.push);
  const [viewport, setViewport] = useState<Viewport>(() =>
    initViewport(window.innerWidth, window.innerHeight)
  );
  const { bulkLoad, lastStrokeId } = useBoard();
  // Debug/e2e hook (whiteboard-multi-user.spec.ts reads stroke count from it)
  (window as any).__useBoard__ = useBoard;
  // Debug/e2e hook: viewport state for fit/pan verification
  (window as any).__bbViewport__ = viewport;
  const { mode, color, sizeIdx, setMode, setSize } = useTool();

  const adminErase = useAdminAreaErase({
    boardId: board?.id,
    token,
    index: useBoard.getState().index,
    onResult: (r) => {
      if (r.ok) {
        if (r.eventId !== undefined) useUndo.getState().push({ kind: 'erase', evId: r.eventId });
        pushToast('success', `已擦除 ${r.erasedCount ?? 0} 条笔画，Ctrl+Z 可撤销。`, 2600);
      } else {
        pushToast('error', r.errorMessage ?? '操作失败，请重试。');
      }
    },
  });
  const adminEraseActive = adminErase.state.kind !== 'idle';

  // ── 禁画状态：auth.user.banned_until 未过期则拦截落笔并显示横幅 ──
  const user2 = useAuth((st) => st.user);
  const bannedUntil = useMemo(() => {
    const t = user2?.banned_until ? new Date(user2.banned_until!).getTime() : 0;
    return t > Date.now() ? t : null;
  }, [user2]);
  const bannedUntilRef = useRef<number | null>(null);
  bannedUntilRef.current = bannedUntil;
  const lastBanRecheckRef = useRef(0);
  const banRecheck = useCallback(async () => {
    const now = Date.now();
    if (now - lastBanRecheckRef.current < 30_000) return;
    lastBanRecheckRef.current = now;
    try {
      const r = await fetch('/api/me', { headers: { authorization: `Bearer ${token}` } });
      if (r.ok) useAuth.setState({ user: await r.json() });
    } catch { /* 离线时下次再查 */ }
  }, [token]);
  const banToastAtRef = useRef(0);
  const blockIfBanned = useCallback(() => {
    if (bannedUntilRef.current == null) return false;
    const now = Date.now();
    if (now - banToastAtRef.current > 4000) {
      banToastAtRef.current = now;
      pushToast('error', '你已被管理员禁止涂鸦，如有疑问请联系管理员。', 3500);
      void banRecheck(); // 顺手查一次是否已解封（节流 30s）
    }
    return true;
  }, [pushToast, banRecheck]);

  // Fetch board meta
  useEffect(() => {
    if (!slug) return;
    resetTileCache();
    setCurrentTileScope(slug);
    useBoard.getState().reset();
    setBoard(null);
    setBoardLoadState('loading');
    let cancelled = false;
    fetch(`/api/boards/${slug}`, { headers: { authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (cancelled) return;
        if (r.ok) {
          const b = await r.json();
          setBoard(b);
          useBoard.getState().setBoard(b);
          setBoardLoadState('ok');
          const t = getTheme(b.theme);
          if (t) useToolStore.setState({ color: t.palette[0] ?? '#ffffff' });
        } else if (r.status === 404 || r.status === 410) {
          setBoardLoadState('not_found');
        } else {
          setBoardLoadState('error');
        }
      })
      .catch(() => {
        if (!cancelled) setBoardLoadState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [slug, token]);

  const theme: ThemeSpec | null = useMemo(() => (board ? getTheme(board.theme) : null), [board]);
  const sizePxMap = useCallback(
    (idx: number, _tool: Tool) => {
      if (!theme) return 4;
      const [min, , max] = theme.sizes;
      return min + (max - min) * (idx - 1) / 9;
    },
    [theme]
  );
  /** 橡皮/悬停拾取用的笔画显示半径（世界单位）——点到墨迹边缘才算命中。 */
  const eraserRadiusOf = useCallback(
    (s: StrokeLike) => sizePxMap(s.size, s.tool) / 2,
    [sizePxMap]
  );

  const wsUrl = useMemo(() => {
    const loc = window.location;
    return `${loc.protocol === 'https:' ? 'wss:' : 'ws:'}//${loc.host}`;
  }, []);
  const client = useMemo(
    () => (slug && token ? new WsClient(wsUrl, token, slug) : null),
    [slug, token, wsUrl]
  );

  // 视口走 ref：loadVisibleTiles 身份保持稳定，否则平移的每个 pointermove
  // 都会重建它并立刻触发下面的加载 effect —— 每步拖动都拉瓦片 + bulkLoad
  // 整屏重绘，这正是"空格拖动非常卡"的根因。防抖 effect 才是加载入口。
  const viewportRef = useRef(viewport);
  viewportRef.current = viewport;

  // Load tiles for current viewport (debounced)
  const loadVisibleTiles = useCallback(async () => {
    if (!slug) return;
    const bb = viewportBBox(viewportRef.current);
    const tiles = tilesInBBox(bb);
    for (const t of tiles) {
      const strokes = await loadTile(slug, token, t.x, t.y);
      if (strokes.length > 0) bulkLoad(strokes);
    }
  }, [slug, token, bulkLoad]);

  useEffect(() => {
    if (!board) return;
    void loadVisibleTiles();
  }, [board, loadVisibleTiles]);

  // 进板默认视角 = 缩放到全部内容（而不是 zoom 1 的局部放大）
  const fitToContentRef = useRef<(() => void) | null>(null);
  const didInitialFitRef = useRef(false);
  useEffect(() => {
    if (!board || didInitialFitRef.current) return;
    didInitialFitRef.current = true;
    fitToContentRef.current?.();
  }, [board]);

  useEffect(() => {
    const h = setTimeout(() => {
      void loadVisibleTiles();
    }, 200);
    return () => clearTimeout(h);
  }, [viewport.cx, viewport.cy, viewport.zoom, loadVisibleTiles]);

  // On WS reopen, backfill delta
  const onReopen = useCallback(() => {
    if (!slug) return;
    loadDelta(slug, token, lastStrokeId).then((ss) => {
      if (ss.length > 0) bulkLoad(ss);
    });
  }, [slug, token, lastStrokeId, bulkLoad]);

  const { liveRef, liveCount, beginStroke, appendPoint, commitStroke, abortStroke, undo, undoCount } = useDrawing(
    client,
    user?.id ?? null,
    sizePxMap,
    onReopen,
    setWsStatus
  );

  // Fatal WS close → if token rejected, logout and bounce to /login
  useEffect(() => {
    if (wsStatus.t !== 'fatal') return;
    if (wsStatus.reason === 'invalid_token') {
      const t = setTimeout(() => {
        logout();
        navigate('/login', { replace: true });
      }, 1500);
      return () => clearTimeout(t);
    }
  }, [wsStatus, logout, navigate]);

  // Resize (viewport CSS px; dpr is handled inside CanvasStage)
  useEffect(() => {
    const onResize = () =>
      setViewport((v) => ({ ...v, screenW: window.innerWidth, screenH: window.innerHeight }));
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  // Viewport report to server
  useEffect(() => {
    if (!client) return;
    const bb = viewportBBox(viewport);
    client.sendViewport(bb.x1 | 0, bb.y1 | 0, bb.x2 | 0, bb.y2 | 0, viewport.zoom);
  }, [client, viewport]);

  // Fit viewport to all committed content
  const index = useBoard((s) => s.index);
  const fitToContent = useCallback(() => {
    // 服务端算的是全板真实 bbox（本地索引只有已加载瓦片——远端内容缺失会把
    // 中心算偏，这是"按 F 复位后偏移"的根因）。30s 缓存避免连按打 DB。
    const fetchBounds = async (): Promise<BBoxT | null> => {
      if (!slug) return null;
      const now = Date.now();
      if (boundsCache && boundsCache.slug === slug && now - boundsCache.at < 30_000) return boundsCache.bbox;
      try {
        const r = await fetch(`/api/boards/${slug}/bounds`, { headers: { authorization: `Bearer ${token}` } });
        if (!r.ok) return null;
        const b = (await r.json()) as { count: number } & BBoxT;
        const bbox = b.count > 0 ? { x1: b.x1, y1: b.y1, x2: b.x2, y2: b.y2 } : null;
        boundsCache = bbox ? { slug: slug!, bbox, at: now } : null;
        return bbox;
      } catch { return null; }
    };
    void (async () => {
      const server = await fetchBounds();
      setViewport((v) => {
        // 本地索引 bbox（含刚落笔未同步的）与服务端 bbox 取并集
        let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
        const all = index.search({ x1: CANVAS_MIN, y1: CANVAS_MIN, x2: CANVAS_MAX, y2: CANVAS_MAX });
        for (const s of all) {
          x1 = Math.min(x1, s.bbox.x1); y1 = Math.min(y1, s.bbox.y1);
          x2 = Math.max(x2, s.bbox.x2); y2 = Math.max(y2, s.bbox.y2);
        }
        if (server) {
          x1 = Math.min(x1, server.x1); y1 = Math.min(y1, server.y1);
          x2 = Math.max(x2, server.x2); y2 = Math.max(y2, server.y2);
        }
        if (x1 === Infinity) return clampViewport({ ...v, cx: 0, cy: 0, zoom: 1 });
        const w = Math.max(1, x2 - x1);
        const h = Math.max(1, y2 - y1);
        // 避开悬浮 UI：顶栏约 64px、底部工具栏约 120px——内容垂直居中于
        // 两者之间的可用区，复位后笔画不再压在工具栏上。
        const topPad = 64, bottomPad = 120, sidePad = 24;
        const availW = Math.max(100, v.screenW - sidePad * 2);
        const availH = Math.max(100, v.screenH - topPad - bottomPad);
        const zoom = Math.max(ZOOM_MIN, Math.min(1.0, 0.9 * Math.min(availW / w, availH / h)));
        const ccx = (x1 + x2) / 2;
        const ccy = (y1 + y2) / 2 + (bottomPad - topPad) / 2 / zoom;
        return clampViewport({ ...v, cx: ccx, cy: ccy, zoom });
      });
    })();
  }, [index, slug, token]);
  fitToContentRef.current = fitToContent;

  // Keyboard shortcuts
  const spaceHeldRef = useRef(false);
  useEffect(() => {
    const kd = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.shiftKey && e.key.toLowerCase() === 'e' && user?.is_admin) {
        adminErase.enter();
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        undo();
        return;
      }
      if (e.key === 'Escape') adminErase.cancel();
      if (e.key === 'b' || e.key === 'B') setMode('pen');
      if (e.key === 'e' || e.key === 'E') setMode('eraser');
      if (e.key === 'x' || e.key === 'X') {
        const next = mode === 'pen' ? 'eraser' : 'pen';
        setMode(next);
        pushToast('info', next === 'pen' ? '画笔' : '橡皮', 1200);
      }
      if (e.key === 'f' || e.key === 'F') fitToContent();
      if (e.key === ' ') {
        e.preventDefault();
        spaceHeldRef.current = true;
      }
      const num = parseInt(e.key, 10);
      if (num >= 1 && num <= 9) setSize(num);
    };
    const ku = (e: KeyboardEvent) => {
      if (e.key === ' ') spaceHeldRef.current = false;
    };
    window.addEventListener('keydown', kd);
    window.addEventListener('keyup', ku);
    return () => {
      window.removeEventListener('keydown', kd);
      window.removeEventListener('keyup', ku);
    };
  }, [user, adminErase, mode, setMode, setSize, undo, fitToContent]);

  // Pointer interaction
  const eraserHintRef = useRef<boolean>(false);
  const draggingRef = useRef<{
    type: 'draw' | 'pan' | 'admin-area';
    lastX: number;
    lastY: number;
  } | null>(null);
  const touchesRef = useRef(new MultiTouchTracker());
  const ghostRef = useRef<BrushGhost>({ x: 0, y: 0, visible: false, r: 0 });

  function currentGhostRadius(): number {
    if (mode === 'eraser') return 12;
    const px = theme ? theme.sizes[0] + (theme.sizes[2] - theme.sizes[0]) * (sizeIdx - 1) / 9 : 4;
    return Math.max(2, (px * viewport.zoom) / 2);
  }

  function updateGhost(e: React.PointerEvent) {
    const g = ghostRef.current;
    if (e.pointerType === 'touch' || adminEraseActive || spaceHeldRef.current) {
      g.visible = false;
      return;
    }
    g.x = e.clientX;
    g.y = e.clientY;
    g.r = currentGhostRadius();
    g.visible = true;
  }

  function onPointerDown(e: React.PointerEvent) {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    if (e.pointerType === 'touch') {
      ghostRef.current.visible = false;
      touchesRef.current.down(e.pointerId, e.clientX, e.clientY);
      if (touchesRef.current.count() >= 2) {
        // Second finger landed — abort the orphan stroke that the first finger
        // started (the user clearly meant to pan/zoom, not draw a short flick).
        if (draggingRef.current?.type === 'draw') abortStroke();
        draggingRef.current = null;
        return;
      }
    }

    if (adminEraseActive) {
      adminErase.beginAt(e.clientX, e.clientY);
      adminErase.updateAt(e.clientX, e.clientY);
      draggingRef.current = { type: 'admin-area', lastX: e.clientX, lastY: e.clientY };
      return;
    }

    if (blockIfBanned()) {
      draggingRef.current = null;
      return;
    }

    if (spaceHeldRef.current || e.button === 1 || e.button === 2) {
      draggingRef.current = { type: 'pan', lastX: e.clientX, lastY: e.clientY };
      return;
    }

    const [wx, wy] = screenToWorld(viewport, e.clientX, e.clientY);
    if (mode === 'eraser') {
      // 精确单条：几何距离命中 + 只取视觉最上层一条，避免 bbox 命中把
      // 附近笔画整片擦掉（管理员误伤他人的重灾区）。
      const hit = index.pickStrokeAt(wx, wy, 10 / viewport.zoom, eraserRadiusOf);
      if (hit && client) {
        if (user.is_admin || hit.user_id === user.id) {
          client.erase([hit.id]);
        } else if (!eraserHintRef.current) {
          eraserHintRef.current = true;
          pushToast('info', '橡皮只能擦你自己的笔画，他人作品由管理员处理。', 3500);
        }
      }
      draggingRef.current = { type: 'draw', lastX: e.clientX, lastY: e.clientY };
      return;
    }

    if (!theme) return;
    draggingRef.current = { type: 'draw', lastX: e.clientX, lastY: e.clientY };
    const pt: Point = { x: wx, y: wy, pressure: e.pressure || 0.5 };
    beginStroke(theme.tool, color, sizeIdx, pt);
  }

  function onPointerMove(e: React.PointerEvent) {
    const [wx, wy] = screenToWorld(viewport, e.clientX, e.clientY);

    updateGhost(e);
    updateHover(e, viewport, index, !!draggingRef.current, adminEraseActive);

    if (e.pointerType === 'touch') {
      const wasCount = touchesRef.current.count();
      const prevC = wasCount >= 2 ? touchesRef.current.centroid() : null;
      const prevS = wasCount >= 2 ? touchesRef.current.spread() : 0;
      touchesRef.current.move(e.pointerId, e.clientX, e.clientY);
      if (touchesRef.current.count() >= 2 && prevC) {
        const newC = touchesRef.current.centroid()!;
        const newS = touchesRef.current.spread();
        setViewport((v) => {
          let nv = pan(v, newC.x - prevC.x, newC.y - prevC.y);
          if (prevS > 0 && newS > 0) nv = zoomAt(nv, newC.x, newC.y, newS / prevS);
          return nv;
        });
        return;
      }
    }

    const d = draggingRef.current;
    if (!d) return;

    if (d.type === 'admin-area') {
      adminErase.updateAt(e.clientX, e.clientY);
      return;
    }
    if (d.type === 'pan') {
      // React 18 对原生输入事件的 state updater 会延迟执行——delta 必须在
      // 更新 d.lastX 之前算好捕获进闭包，否则 updater 里读到的新 lastX 会把
      // 位移算成 0（"空格拖动每次只能移动一点点"的根因）。
      const dx = e.clientX - d.lastX;
      const dy = e.clientY - d.lastY;
      d.lastX = e.clientX;
      d.lastY = e.clientY;
      setViewport((v) => pan(v, dx, dy));
      return;
    }
    if (d.type === 'draw') {
      if (mode === 'eraser') {
        const hit = index.pickStrokeAt(wx, wy, 10 / viewport.zoom, eraserRadiusOf);
        if (hit && client && (user.is_admin || hit.user_id === user.id)) client.erase([hit.id]);
        return;
      }
      // High-frequency digitizers (Apple Pencil, 120Hz phones) batch samples
      // into one pointermove; replay every coalesced point so fast strokes
      // stay smooth instead of turning into line segments.
      const native = e.nativeEvent as PointerEvent & {
        getCoalescedEvents?: () => PointerEvent[];
      };
      const coalesced = native.getCoalescedEvents?.() ?? [];
      const samples = coalesced.length > 0 ? coalesced : [native];
      for (const ev of samples) {
        const [x, y] = screenToWorld(viewport, ev.clientX, ev.clientY);
        appendPoint({ x, y, pressure: ev.pressure || 0.5 });
      }
    }
  }

  async function onPointerUp(e: React.PointerEvent) {
    if (e.pointerType === 'touch') touchesRef.current.up(e.pointerId);
    const d = draggingRef.current;
    draggingRef.current = null;
    if (!d) return;

    if (d.type === 'admin-area') {
      adminErase.finalize(viewport);
      return;
    }
    if (d.type === 'draw' && mode === 'pen') commitStroke();
  }

  function onPointerLeave(e: React.PointerEvent) {
    clearHover();
    ghostRef.current.visible = false;
  }

  function onWheel(e: React.WheelEvent) {
    setViewport((v) => zoomAt(v, e.clientX, e.clientY, e.deltaY < 0 ? 1.1 : 1 / 1.1));
  }

  // Prevent wheel bubbling / default via native listener
  useEffect(() => {
    const h = (e: WheelEvent) => e.preventDefault();
    window.addEventListener('wheel', h, { passive: false });
    return () => window.removeEventListener('wheel', h);
  }, []);

  const highlightUserId = usePresence((s) => s.highlightUserId);

  if (!theme || !board) {
    return <BoardLoadingStates state={boardLoadState} navigate={navigate} />;
  }

  const cursorClass = adminEraseActive
    ? 'cursor-crosshair'
    : spaceHeldRef.current
    ? 'cursor-grab'
    : mode === 'pen' || mode === 'eraser'
    ? 'cursor-none'
    : 'cursor-crosshair';

  return (
    <div
      className={`relative h-full w-full overflow-hidden ${cursorClass}`}
      style={{ background: theme.bgColor }}
    >
      <ConnectionBanner status={wsStatus} />
      {bannedUntil != null && (
        <div
          role="alert"
          className="fixed top-14 left-1/2 -translate-x-1/2 z-40 max-w-[92vw] rounded-full bg-red-600/95 text-white px-4 py-1.5 text-[13px] font-medium shadow-elev1 flex items-center gap-2"
        >
          <span aria-hidden>🚫</span>
          <span>
            你已被禁止涂鸦，至{' '}
            {new Date(bannedUntil).toLocaleString('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            ，如有疑问请联系管理员
          </span>
        </div>
      )}
      <OnboardingHint theme={theme} initialOpen={onboardingInitiallyOpen} />
      {guidelinesOpen && (
        <GuidelinesDialog theme={theme} onAgree={() => setGuidelinesOpen(false)} />
      )}
      {index.size() === 0 && liveCount === 0 && (
        <EmptyBoardWatermark theme={theme} />
      )}
      <ConfirmDialog
        open={adminErase.state.kind === 'pending'}
        title="确认擦除选区？"
        destructive
        confirmLabel="擦除"
        theme={theme}
        body={
          adminErase.state.kind === 'pending' ? (
            <>
              选区内可见笔画约 <b>{adminErase.state.count}</b> 条（实际数量以服务端为准）。
              <br />
              擦错不要慌：Ctrl+Z 可立即撤销，后台审计里也可回滚。
            </>
          ) : null
        }
        onCancel={adminErase.cancel}
        onConfirm={adminErase.confirm}
      />
      <ToastViewport />
      <CanvasStage
        theme={theme}
        viewport={viewport}
        index={index}
        liveRef={liveRef}
        ghostRef={ghostRef}
        selfUserId={user.id}
        highlightUserId={highlightUserId}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onWheel={onWheel}
        onPointerLeave={onPointerLeave}
      />
      {hoveredAuthor && (
        <div
          aria-hidden
          className="fixed z-50 pointer-events-none -translate-x-1/2 -translate-y-[140%] inline-flex items-center gap-1.5 rounded-full bg-black/75 backdrop-blur-md px-2.5 py-1 text-[12px] font-medium text-white shadow-elev1"
          style={{ left: hoveredAuthor.sx, top: hoveredAuthor.sy }}
        >
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ background: hoveredAuthor.color }}
          />
          {hoveredAuthor.name}
          {user.is_admin && hoveredAuthor.userId && (
            <span className="text-[10px] font-mono opacity-60">#{hoveredAuthor.userId.slice(0, 8)}</span>
          )}
        </div>
      )}
      <BoardSwitcher />
      <UserList />
      <Toolbar
        theme={theme}
        isAdmin={user.is_admin}
        boardName={board?.name}
        onEnterAreaErase={user.is_admin ? adminErase.enter : undefined}
        onUndo={undo}
        canUndo={undoCount > 0}
        onFit={fitToContent}
      />
      <AdminEraseOverlay state={adminErase.state} active={adminEraseActive} />
    </div>
  );
}
